/**
 * DepartmentPicker - Wrapper around generic EntityPicker
 * 
 * Provides department-specific configuration and maintains backward compatibility
 * with any code that imports DepartmentPickerField or DepartmentPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';

// ── Department-specific configuration ────────────────────────────────────────
export const DEPARTMENT_CONFIG = {
  apiEndpoint: "/api/Department/active",
  entityName: "Department",
  idField: "id",
  displayFields: {
    primary: "description",
    secondary: "code"
  },
  tableHeaders: ["Code", "Description"],
  tableAccessors: ["code", "description"],
  searchFields: [
    { label: "All", field: "all", index: 0 },
    { label: "Description", field: "description", index: 1 },
    { label: "Code", field: "code", index: 2 },
  ],
  placeholder: "--Select department--"
};

// ── DepartmentPickerSelect wrapper ───────────────────────────────────────────
/**
 * DepartmentPickerSelect
 * 
 * Usage:
 * <DepartmentPickerSelect
 *   label="Department"
 *   departmentId={form.departmentId}
 *   onSelect={(val) => setDepartment(val)}
 *   placeholder="All Departments"
 * />
 */
export function DepartmentPickerSelect({
  label,
  value,   // Only ID is required for display/matching
  onSelect,
  onClear,
  placeholder = "--Select department--",
  allowAll,
  disabled = false,
  isRequired = false,
  className = "",
  error = false,
  errorMessage = ""
}) {
  return (
    <div className="mb-1 w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <EntityPickerSelect
        config={DEPARTMENT_CONFIG}
        // Pass an object with the ID. 
        // EntityPickerSelect matches this against its internal list.
        value={value ? { id: value } : null}
        disabled={disabled}
        placeholder={placeholder}
        allowAll={allowAll}
        className={className}
        onChange={(val) => {
          if (val === null) {
            if (onClear) onClear();
          } else {
            // Returns the full object {id, code, description}
            if (onSelect) onSelect(val);
          }
        }}
      />

      {/* Error message display */}
      {error && (
        <span className="mt-1 block text-sm text-red-500">
          {errorMessage}
        </span>
      )}
    </div>
  );
}

// ── DepartmentPickerField wrapper ────────────────────────────────────────────
/**
 * DepartmentPickerField
 * 
 * Usage:
 * <DepartmentPickerField
 *   label="Department"
 *   departmentId={form.departmentId}
 *   displayValue={form.departmentDescription}
 *   onSelect={(department) => {
 *     set("departmentId", department.id);
 *     set("departmentCode", department.code);
 *     set("departmentDescription", department.description);
 *   }}
 *   onClear={() => {
 *     set("departmentId", "");
 *     set("departmentCode", "");
 *     set("departmentDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function DepartmentPickerField({
  departmentId,       // department-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={DEPARTMENT_CONFIG}
      entityId={departmentId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── DepartmentPickerModal wrapper ────────────────────────────────────────────
/**
 * DepartmentPickerModal
 * 
 * Usage (standalone):
 * <DepartmentPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(department) => {
 *     set("departmentId", department.id);
 *     set("departmentDescription", department.description);
 *   }}
 * />
 */
export function DepartmentPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={DEPARTMENT_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { DepartmentPickerField as default };